import { ATTRIBUTION_LABELS, type AttributionKey } from '../attribution.js';
import type { GuideFiche } from './types.js';

/**
 * Fiches des règles de cartes « À faire » (« pourquoi j'ai cette carte ? »), écrites à partir de
 * `packages/shared/src/rules.ts` (`evaluateRules`, `AUTO_CLOSE_RULES`) et des réglages `settings.rules`
 * (`DEFAULT_RULES`). Une règle ne fait que PROPOSER : rien n'est exécuté sans validation (CDC §0).
 *
 * Principes communs : une carte ouverte est mise à jour tant que sa condition tient ; faite ou écartée, elle ne
 * revient jamais (seules les règles à cycles proposent une nouvelle carte au cycle suivant) ; les règles qui
 * se ferment seules le font au passage des règles qui suit la synchro.
 */

const SETTINGS = 'Réglages > Règles des actions';

/**
 * Lot AK : « la personne réglée pour « Facturer » » : une personne par type de carte (Réglages > Règles des actions,
 * « Qui reçoit les cartes » ; jamais réglé : l'ancien groupe, puis l'administrateur).
 */
const who = Object.assign((key: AttributionKey) => `la personne réglée pour « ${ATTRIBUTION_LABELS[key]} »`, {
  setting: (key: AttributionKey) =>
    `Qui reçoit « ${ATTRIBUTION_LABELS[key]} » (${SETTINGS}, Qui reçoit les cartes)`,
});
const ZERO = 'Jamais pour un devis ou une facture à 0 € (moins de 0,50 € HT).';

export const RULE_FICHES = [
  {
    id: 'regle.quote_followup',
    kind: 'rule',
    ruleKey: 'quote_followup',
    name: 'Relancer un devis',
    screens: ['À faire'],
    summary: 'Un devis envoyé attend une réponse depuis trop longtemps.',
    calculation:
      "Apparaît pour un devis « en attente » N jours après son envoi (date d'envoi, sinon date du devis ; N = 10 par défaut), puis tous les M jours après la dernière relance envoyée (M = 10). Les échanges Gmail et Axonaut du client depuis l'envoi comptent : un mail envoyé au client vaut relance ; un mail du client arrivé après la dernière relance fait apparaître « X a répondu » tout de suite. Après « Valider et envoyer », la carte attend la réponse ; une réponse du client la fait passer à « réponse à traiter ». Se ferme seule quand le devis n'est plus en attente (accepté, refusé). Pour " +
      who('quoteFollowup') +
      ", sauf si le client a son responsable des relances : réattribuer une carte « Relancer le devis » du client à quelqu'un d'autre lui confie les relances de devis suivantes de ce client (les autres cartes du client suivent leur type, les cartes déjà ouvertes gardent leur personne) ; mention « Relances de ce client » sur la carte et sur la fiche du projet, « Retirer » pour revenir à la personne des Réglages (réattribuer à cette personne l'efface aussi). " +
      ZERO,
    data: [
      { what: 'Devis en attente', source: 'axonaut' },
      { what: 'Échanges avec le client', source: 'gmail' },
      { what: 'Événements e-mail Axonaut', source: 'axonaut' },
    ],
    settings: [`Premier délai et délai entre relances (${SETTINGS})`, who.setting('quoteFollowup')],
    freshness: ['axonaut', 'gmail'],
    related: ['chiffres.devis-en-attente', 'synchro.gmail'],
  },
  {
    id: 'regle.invoice_create.deposit',
    kind: 'rule',
    ruleKey: 'invoice_create.deposit',
    name: 'Facturer l’acompte',
    screens: ['À faire'],
    summary: 'Un devis vient d’être signé et son acompte n’est pas facturé.',
    calculation:
      "Devis accepté depuis moins de 30 jours sans facture d'acompte : ni facture rattachée au devis (hors abonnement), ni facture du client non rattachée émise depuis la signature (− 3 jours). Échéance : signature + 2 jours. Montant : 50 % du devis ; sous 1 000 € HT, une facture unique du montant total (« Facturer »). La facture se crée à la main dans Axonaut (jamais par le Cockpit). Se ferme seule dès qu'une facture apparaît. Pour " +
      who('invoice') +
      '. ' +
      ZERO,
    data: [{ what: 'Devis acceptés et factures', source: 'axonaut' }],
    settings: [`Délai après signature, seuil de la facture unique (${SETTINGS})`, who.setting('invoice')],
    freshness: ['axonaut'],
    related: ['regle.invoice_create.balance', 'afaire.en-jeu'],
  },
  {
    id: 'regle.invoice_create.balance',
    kind: 'rule',
    ruleKey: 'invoice_create.balance',
    name: 'Facturer le solde',
    screens: ['À faire'],
    summary: 'Le site est en ligne et le solde du devis n’est pas facturé.',
    calculation:
      "Pour un devis accepté qui a une facture d'acompte mais ni solde ni facture unique, quand le site du client est en ligne : date de mise en ligne lue dans le back Evolusite, ou étape « Mise en ligne » cochée sur le projet (date du R5 tenu, sinon aujourd'hui). Montant : devis − acomptes facturés. Échéance : mise en ligne + 0 jour. Se ferme seule quand le solde est facturé. Pour " +
      who('invoice') +
      '. ' +
      ZERO,
    data: [
      { what: 'Devis et factures (acompte, solde)', source: 'axonaut' },
      { what: 'Date de mise en ligne du site', source: 'evolusite' },
      { what: 'Étape « Mise en ligne » cochée', source: 'cockpit' },
    ],
    settings: [`Délai après la mise en ligne (${SETTINGS})`, who.setting('invoice')],
    freshness: ['axonaut', 'evolusite'],
    related: ['regle.invoice_create.deposit'],
  },
  {
    id: 'regle.subscription_setup',
    kind: 'rule',
    ruleKey: 'subscription_setup',
    name: 'Mettre en place l’abonnement',
    screens: ['À faire'],
    summary: 'Le client devrait avoir un abonnement et n’en a pas.',
    calculation:
      "Deux déclencheurs : (1) le R5 du projet est passé (R5 réellement fixé ; un R5 encore en période ne compte pas) ou l'étape « R5 · formation » est cochée, depuis moins de 90 jours ; (2) un site du back en ligne et rattaché au client, sans limite d'ancienneté. Dans les deux cas, sans abonnement en place : Stripe actif ou en essai (déclencheur 1), tout abonnement Stripe non résilié (déclencheur 2), ou un abonnement hors Stripe en cours. Le déclencheur (2) ignore un client qui a un R5 à venir (projet non terminé dont le R5 n'a pas encore eu lieu, ou R5 à venir dans l'agenda) : son site peut être déployé avant le R5 ; après un R5 de l'agenda, l'échéance est le R5 + le délai réglé. Une seule carte par client ; la ligne « Règle » dit quel déclencheur a joué (« abonnement après le R5 » ou « site en ligne sans abonnement »). Se ferme seule quand l'abonnement apparaît ou quand plus aucun déclencheur ne joue (par exemple un R5 de nouveau à venir), et revient alors sous une nouvelle carte si un déclencheur rejoue ; faite ou écartée par quelqu'un, elle ne revient jamais. « Pas d'abonnement à mettre » (à la place d'« Écarter ») : « Client sans abonnement » (le client est marqué, plus jamais de carte, mention réversible sur sa fiche et dans Réglages) ; « Client parti » (date, compté dans « Clients perdus » de Chiffres à cette date, lien vers la page du site dans le super admin Evolusite pour le supprimer à la main) ; « Abonnement programmé le J » (plus de carte jusqu'à J + 3 jours, puis une nouvelle carte seulement si aucun abonnement n'est en place, qui se ferme seule dès qu'il apparaît). Pour " +
      who('subscriptionSetup') +
      '.',
    data: [
      { what: 'Abonnements Stripe', source: 'evolusite' },
      { what: 'Sites en ligne du back', source: 'evolusite' },
      { what: 'Abonnements hors Stripe', source: 'manual' },
      { what: 'R5 du projet, étapes', source: 'cockpit' },
      { what: 'R5 de l’agenda', source: 'calendar' },
    ],
    settings: [`Délai après le R5 (${SETTINGS})`, who.setting('subscriptionSetup')],
    excludes: [
      'Sites ignorés (« pas un client »)',
      'Clients marqués « Pas d’abonnement à mettre » (sans abonnement, partis, abonnement programmé en attente)',
      'Déclencheur « site en ligne » : clients dont le R5 est à venir',
    ],
    freshness: ['evolusite', 'calendar'],
    related: ['chiffres.abonnements', 'chiffres.clients'],
  },
  {
    id: 'regle.unpaid_reminder',
    kind: 'rule',
    ruleKey: 'unpaid_reminder',
    name: 'Rappel de paiement',
    screens: ['À faire'],
    summary: 'Une facture est échue et pas payée.',
    calculation:
      'Facture non payée N jours après son échéance (N = 5), puis tous les M jours après le rappel précédent (M = 7). Montant : reste dû. Se ferme seule quand la facture est payée. Pour ' +
      who('unpaid') +
      '. Jamais pour une facture à 0 € ni un reste dû à 0 €.',
    data: [{ what: 'Factures (échéance, reste dû)', source: 'axonaut' }],
    settings: [`Délai après l'échéance et entre rappels (${SETTINGS})`, who.setting('unpaid')],
    freshness: ['axonaut'],
    related: ['chiffres.reste-a-encaisser', 'tresorerie.factures-non-comptees'],
  },
  {
    id: 'regle.payment_failed',
    kind: 'rule',
    ruleKey: 'payment_failed',
    name: 'Paiement refusé',
    screens: ['À faire'],
    summary: 'Stripe signale un abonnement impayé.',
    calculation:
      "Apparaît dès que le back Evolusite donne une date d'impayé pour l'abonnement Stripe d'un site (prélèvement refusé). Montant : mensuel (× 12 pour un abonnement annuel). Une carte par impayé ; elle disparaît quand le back ne signale plus d'impayé. Pour " +
      who('unpaid') +
      '.',
    data: [{ what: 'Abonnements Stripe (date d’impayé, prochaine tentative)', source: 'evolusite' }],
    settings: [who.setting('unpaid')],
    freshness: ['evolusite'],
    related: ['chiffres.abonnements'],
  },
  {
    id: 'regle.review_request',
    kind: 'rule',
    ruleKey: 'review_request',
    name: 'Demander un avis Google',
    screens: ['À faire'],
    summary: 'Le projet est livré : moment de demander un avis.',
    calculation:
      "Réglage « après le R5 » (par défaut) : N jours (7) après le dernier R5 passé du client, pris dans l'agenda, sinon dans le projet (R5 fixé, sinon date du planning). Réglage « après la mise en ligne » : N jours après la mise en ligne du site lue dans le back. Seuls les R5 et mises en ligne des 60 derniers jours déclenchent. Jamais deux demandes au même client (même une ancienne carte par site). Pour " +
      who('review') +
      '.',
    data: [
      { what: 'Rendez-vous R5', source: 'calendar' },
      { what: 'Dates des projets', source: 'cockpit' },
      { what: 'Mise en ligne du site', source: 'evolusite' },
    ],
    settings: [`Délai et point de départ : R5 ou mise en ligne (${SETTINGS})`, who.setting('review')],
    freshness: ['calendar', 'evolusite'],
  },
  {
    id: 'regle.quote_to_send',
    kind: 'rule',
    ruleKey: 'quote_to_send',
    name: 'Envoyer le devis',
    screens: ['À faire'],
    summary: 'Un rendez-vous a eu lieu et aucun devis n’a suivi.',
    calculation:
      "N jours (0 par défaut) après un R2 (réglage par défaut) ou un R1 de l'agenda dont le client est connu, s'il n'a aucun devis daté de ce rendez-vous ou après (pour un R2 : depuis le dernier R1 qui le précède). « Pas de R2 pour ce client » sur la carte « Fixer le R2 » fait apparaître la carte aussitôt. Un rendez-vous de plus de 60 jours ne fait plus naître de carte (lot BM : la reprise de l'historique des agendas ne réveille pas les vieux prospects), sauf « Pas de R2 pour ce client ». Une seule carte par client. Se ferme seule quand un devis apparaît. Le bouton « Préparer le devis » ouvre le même brouillon de devis que la carte « Préparer le devis » d'après le R1 (fiche devis.brouillon), repris s'il existe déjà (pastille « Brouillon en cours ») ; le devis est créé dans Axonaut seulement à la validation. Pour " +
      who('quoteToSend') +
      '.',
    data: [
      { what: 'Rendez-vous R1 et R2', source: 'calendar' },
      { what: 'Devis', source: 'axonaut' },
    ],
    settings: [`Délai et rendez-vous de départ : R1 ou R2 (${SETTINGS})`, who.setting('quoteToSend')],
    freshness: ['calendar', 'axonaut'],
    related: ['regle.meeting_r2_missing', 'regle.quote_prepare', 'devis.brouillon'],
  },
  {
    id: 'regle.meeting_r2_missing',
    kind: 'rule',
    ruleKey: 'meeting_r2_missing',
    name: 'Fixer le R2',
    screens: ['À faire'],
    summary: 'Un R1 a eu lieu et aucun R2 n’est prévu.',
    calculation:
      "R1 passé depuis N jours (7) et au plus 60 jours, sans R2 du client dans l'agenda depuis (même à venir), sans devis depuis le R1, sans « Pas de R2 pour ce client ». Une carte par client (le R1 le plus récent), jamais en même temps que « Envoyer le devis ». Se ferme seule quand un R2 ou un devis apparaît ; « Pas de R2 pour ce client » (annulable) la remplace par « Envoyer le devis ». Le bouton « Fixer le R2 » prépare le rendez-vous : premier créneau libre de l'agenda du propriétaire du R1 (sinon de la personne des rendez-vous), date et heure modifiables, titre de « Rendez-vous clients », invitation du client décochable. Pour " +
      who('quoteToSend') +
      '.',
    data: [
      { what: 'Rendez-vous R1 et R2', source: 'calendar' },
      { what: 'Devis', source: 'axonaut' },
    ],
    settings: [`Délai après le R1 (${SETTINGS})`, who.setting('quoteToSend')],
    freshness: ['calendar', 'axonaut'],
    related: ['regle.quote_to_send'],
  },
  {
    id: 'regle.deadline',
    kind: 'rule',
    ruleKey: 'deadline',
    name: 'Échéance à venir',
    screens: ['À faire'],
    summary:
      'Une échéance de l’onglet « Échéances » du Google Sheet (renouvellement de domaine, facture annuelle…) approche.',
    calculation:
      "Apparaît N jours avant la date de l'échéance (30 par défaut), à faire une semaine avant (au plus tard la veille). Les échéances viennent de l'onglet « Échéances » du Google Sheet, lues à l'import (un nouvel import remplace les précédentes ; celles saisies dans le Cockpit restent). Pour " +
      who('invoice') +
      ' si le libellé parle de facture, sinon pour ' +
      who('deadline') +
      ". Se ferme seule quand l'échéance est marquée faite.",
    data: [
      { what: 'Échéances de l’onglet « Échéances »', source: 'sheet' },
      { what: 'Échéances saisies', source: 'manual' },
    ],
    settings: [
      `« Échéances du Google Sheet : afficher la carte dans À faire N jours avant » (${SETTINGS})`,
      who.setting('deadline'),
    ],
    freshness: ['sheet', 'manual'],
  },
  {
    id: 'regle.info.anniversary',
    kind: 'rule',
    ruleKey: 'info.anniversary',
    name: 'Anniversaire de mise en ligne',
    screens: ['À faire'],
    summary: 'Information : un site fête ses 1, 3 ou 5 ans.',
    calculation:
      'Date de mise en ligne lue dans le back (sinon date de démarrage du Sheet) + 1, 3 ou 5 ans ; la carte apparaît de 7 jours avant à 14 jours après. Information seulement, pour ' +
      who('review') +
      '.',
    data: [
      { what: 'Mise en ligne des sites', source: 'evolusite' },
      { what: 'Date de démarrage du client', source: 'sheet' },
    ],
    settings: [`Années fêtées (${SETTINGS})`, who.setting('review')],
    freshness: ['evolusite'],
  },
  {
    id: 'regle.project_plan',
    kind: 'rule',
    ruleKey: 'project_plan',
    name: 'Préparer le projet / Rattacher le devis au projet',
    screens: ['À faire', 'Projets'],
    summary: 'Un devis de site vient d’être signé et n’est pas au planning.',
    calculation:
      "Devis accepté depuis moins de 30 jours, contenant une ligne vitrine, e-commerce ou refonte, sans projet qui lui soit rattaché. Échéance : signature + 1 jour. Deux variantes : si le client a déjà un projet en cours sans devis (souvent importé du Sheet), la carte devient « Rattacher le devis au projet » (bouton principal : rattacher ; secondaire « C'est un autre projet » → Préparer le projet) ; sinon « Préparer le projet » (ouvre le simulateur). « Pas de projet pour ce devis » (action secondaire, réversible dans le simulateur) fait ignorer ce devis. Se ferme seule quand un projet est rattaché au devis ; supprimer un projet préparé sans « Ne plus proposer » rouvre la carte. Pour " +
      who('projectPlan') +
      '. ' +
      ZERO,
    data: [
      { what: 'Devis acceptés (lignes)', source: 'axonaut' },
      { what: 'Projets du planning', source: 'cockpit' },
    ],
    settings: [
      `Délai après signature (${SETTINGS})`,
      'Catégories de produits (Réglages)',
      who.setting('projectPlan'),
    ],
    freshness: ['axonaut'],
    related: ['projets.simulateur'],
  },
  {
    id: 'regle.expense_categorize',
    kind: 'rule',
    ruleKey: 'expense_categorize',
    name: 'Catégoriser des dépenses',
    screens: ['À faire', 'Dépenses'],
    summary: 'Des lignes bancaires sont arrivées sans poste.',
    calculation:
      "Une seule carte à la fois, tant qu'il reste des lignes à catégoriser (fiche depenses.a-categoriser) ; faite ou écartée, une nouvelle carte n'arrive qu'avec de nouvelles lignes. À faire dans 2 jours, pour " +
      who('expenseCategorize') +
      '. Se ferme seule quand tout est rangé.',
    data: [{ what: 'Lignes sans poste', source: 'axonaut' }],
    settings: [who.setting('expenseCategorize')],
    freshness: ['axonaut'],
    related: ['depenses.a-categoriser'],
  },
  {
    id: 'regle.partner_missing',
    kind: 'rule',
    ruleKey: 'partner_missing',
    name: 'Renseigner le canal dans Axonaut',
    screens: ['À faire', 'Apporteurs'],
    summary: 'Des devis signés cette année n’ont ni apporteur ni canal d’acquisition.',
    calculation:
      "Une seule carte qui liste les devis acceptés de l'année dont on ne sait pas d'où vient le client : ni canal ni apporteur sur le devis, ni sur le client (champs « Canal d'acquisition » et « Apporteur d'affaire » de la fiche Axonaut, sinon la saisie du Cockpit). Un seul des deux suffit. La carte renvoie vers les fiches à compléter dans Axonaut : le Cockpit n'écrit jamais dans Axonaut. Se ferme seule quand la synchro voit la valeur. Faite ou écartée, une nouvelle carte n'arrive qu'avec un nouveau devis concerné. Pour " +
      who('partner') +
      '. ' +
      ZERO,
    data: [
      { what: 'Devis acceptés, fiches clients (canal, apporteur)', source: 'axonaut' },
      { what: 'Saisies du Cockpit', source: 'manual' },
    ],
    settings: [who.setting('partner')],
    freshness: ['axonaut'],
    related: ['apporteurs.devis-sans-apporteur', 'chiffres.acquisition', 'regle.partner_unknown'],
  },
  {
    id: 'regle.partner_unknown',
    kind: 'rule',
    ruleKey: 'partner_unknown',
    name: 'Apporteurs Axonaut à reconnaître',
    screens: ['À faire', 'Apporteurs'],
    summary:
      'Des noms tapés dans le champ « Apporteur d’affaire » d’Axonaut ne sont pas encore des apporteurs du Cockpit.',
    calculation:
      "Une seule carte « Apporteurs Axonaut à reconnaître : N noms », où N = les noms distincts du champ « Apporteur d'affaire » des fiches Axonaut qu'aucun apporteur du Cockpit ne porte (ni son nom, ni un autre nom enregistré ; comparaison sans accents, casse, apostrophes ni ponctuation, « & » vaut « et ») et qui ne sont pas ignorés. Les valeurs sont montrées telles quelles, jamais découpées (certaines mêlent canal, agence et personne). Le titre suit à chaque synchro ; la carte se ferme seule quand il n'en reste plus. Faite ou écartée, une nouvelle carte n'arrive que si un nom qui n'y figurait pas apparaît. Son bouton ouvre la liste « Noms d'apporteurs inconnus » de l'écran Apporteurs : « Créer l'apporteur » (le nom Axonaut est retenu comme autre nom s'il diffère), « Rattacher à… » (le nom devient un autre nom de l'apporteur choisi) ou « Ignorer ce nom » (il ne compte plus ; réversible depuis la liste). Rien n'est écrit dans Axonaut. Pour " +
      who('partner') +
      '.',
    data: [
      { what: 'Champ « Apporteur d’affaire » des fiches clients', source: 'axonaut' },
      { what: 'Apporteurs, autres noms et noms ignorés', source: 'manual' },
    ],
    settings: [who.setting('partner')],
    freshness: ['axonaut'],
    related: ['apporteurs.partenaires', 'apporteurs.noms-inconnus', 'chiffres.acquisition'],
  },
  {
    id: 'regle.r1_prep',
    kind: 'rule',
    ruleKey: 'r1_prep',
    name: 'Compléter le R1',
    screens: ['À faire', 'Agenda'],
    summary: 'Un R1 approche : dire ce que l’on sait du prospect avant que la fiche R1 soit préparée.',
    calculation:
      "Apparaît N jours avant un R1 de l'agenda (3 par défaut ; tout de suite si le R1 est plus proche), tant que le R1 est à venir, que les fiches R1 sont activées et qu'aucun membre n'a enregistré d'infos ni choisi « Rien à ajouter ». Un R1 présent dans plusieurs agendas compte une fois. Une carte par membre présent sur le R1 : les membres dont l'agenda contient le rendez-vous, plus les membres de l'équipe invités. « Compléter » ouvre le formulaire sous la carte : aucun champ n'est obligatoire ; ce qui est déjà écrit dans la description du rendez-vous arrive pré-rempli (en vert, « trouvé dans l'agenda »), lu une fois par Claude (effort bas, compté dans le plafond de l'IA). « Enregistrer » ou « Rien à ajouter » ferme la carte de la personne ; celles des autres membres se ferment seules au passage des règles qui suit. Se ferme seule aussi quand le R1 est passé ou supprimé, ou quand les fiches R1 sont désactivées. Due la veille du R1. Le canal et l'apporteur saisis servent aussi au tableau « D'où viennent les clients ». Pour les membres présents, ou pour " +
      who('r1Prep') +
      ' si elle est réglée. Rien n’est écrit hors du Cockpit.',
    data: [
      { what: 'Rendez-vous R1 (titre, invités, description)', source: 'calendar' },
      { what: 'Infos saisies sur la carte', source: 'manual' },
    ],
    settings: [
      'Fiches R1 activées et délai de la demande (Réglages > Projets > Fiches R1)',
      who.setting('r1Prep'),
    ],
    freshness: ['calendar'],
    related: ['chiffres.acquisition'],
  },
  {
    id: 'regle.quote_prepare',
    kind: 'rule',
    ruleKey: 'quote_prepare',
    name: 'Préparer le devis',
    screens: ['À faire', 'Agenda'],
    summary: 'Un R1 a eu lieu : préparer le devis en interne avant le R2.',
    calculation:
      "Apparaît N jours après un R1 de l'agenda (0 par défaut : dès que le R1 a commencé), au plus 30 jours après, que le prospect soit déjà dans Axonaut ou non. Un R1 présent dans plusieurs agendas compte une fois ; une série récurrente, sa dernière occurrence passée. Une carte par membre présent sur le R1 (les membres dont l'agenda contient le rendez-vous, plus les membres de l'équipe invités), ou pour la seule personne réglée dans « Préparer les devis » ; sans membre présent connu : " +
      who('quoteToSend') +
      ". Son bouton « Préparer le devis » ouvre le brouillon de devis du R1 (fiche devis.brouillon), le même pour tous les membres. Se ferme seule quand le devis est créé dans Axonaut par ce parcours, quand le brouillon est écarté (« Écarter le brouillon »), ou quand un devis du client daté du R1 ou après apparaît dans Axonaut (client connu : relié à la main au R1, sinon reconnu par l'adresse d'un invité ou noté à la pose du rendez-vous ; jamais par le nom). « Devis déjà fait » (le client n'a pas été reconnu alors qu'un devis existe) : chercher le client Axonaut, puis choisir son devis (récents d'abord : numéro, date, montant, statut, « Ouvrir dans Axonaut ») ; à la validation, le R1 est relié à ce client et TOUTES les cartes « Préparer le devis » de ce R1 (une par membre présent) sont faites, avec le devis noté dans « Fait cette semaine ». Le devis peut être daté d'avant le R1 : le choix fait foi, et plus aucune carte ne revient pour ce R1 (« Annuler » oublie le devis choisi, le client reste relié). Sans devis chez ce client : « Relier le R1 à ce client » seulement, la carte reste ouverte et prend le nom du client. Faite ou écartée, la carte d'une personne ne revient pas. La carte « Envoyer le devis » (après le R2 par défaut) reste celle de l'envoi. Rien n'est écrit hors du Cockpit.",
    data: [
      { what: 'Rendez-vous R1 (titre, membres présents)', source: 'calendar' },
      { what: 'Brouillons de devis, client relié à la main, « Devis déjà fait »', source: 'manual' },
      { what: 'Devis', source: 'axonaut' },
    ],
    settings: [`Délai après le R1 (${SETTINGS})`, who.setting('quotePrepare')],
    freshness: ['calendar', 'axonaut'],
    related: ['devis.brouillon', 'regle.quote_to_send', 'regle.r1_prep', 'synchro.agendas'],
  },
  {
    // Lot AT : pas une règle, l'écran du brouillon de devis (carte « Préparer le devis », volet du R1).
    id: 'devis.brouillon',
    kind: 'rule',
    name: 'Brouillon de devis après le R1',
    screens: ['À faire', 'Agenda'],
    summary:
      'Le devis préparé dans le Cockpit à partir des notes du R1, puis créé dans Axonaut à la validation.',
    calculation:
      "S'ouvre depuis le bloc « Après le R1 » du volet du R1 (Agenda) ou depuis les cartes « Préparer le devis » et « Envoyer le devis ». Un brouillon par R1 (sinon par client), enregistré au fil de l'eau : rien n'existe dans Axonaut avant la validation. 1. Coller ses notes ou la transcription du rendez-vous (texte), puis « Proposer un devis » : Claude lit les notes, la fiche R1, les infos du R1, le catalogue Axonaut (les produits utilisés sur un devis des 12 derniers mois, les produits de frais et d'abonnement, les produits apparus récemment ; tout le catalogue actif si la liste est trop courte), 4 ou 5 devis récents comme modèles (signés d'abord) et le panier médian des devis signés ; coût compté dans le plafond de l'IA. Les notes sont gardées avec le brouillon. Le devis proposé a la forme des vrais devis : une ligne de frais (« 01 - Création d'un site vitrine », « 15 - Intégration site vitrine » pour un projet partenaire, « 16 - Migration depuis WordPress ») dont la description dit tout le projet (CMS Evolusite, back office, maquette, référencement, structure des pages, fonctionnalités particulières), au prix des notes sinon du panier médian ; une ligne d'abonnement « 99 - Abonnement Optimum » ou « Optimum + » à 0 € (elle fait signer le contrat du prélèvement ; sans indice : Optimum +, « abonnement à confirmer ») ; une option (boîte mail…) seulement si les notes la demandent. Le devis Axonaut reçoit le chapitre d'usage (« FRAIS DE CRÉATION », « FRAIS D'INTÉGRATION », « FRAIS DE MIGRATION »). 2. Le devis proposé : chaque ligne est un produit du catalogue (recherche pour en ajouter), une quantité, un prix unitaire HT, une remise en % (100 % = offert) ; titre, description et prix restent modifiables. Sous chaque ligne, « Pourquoi » cite la phrase des notes qui la justifie, et « à vérifier » signale un doute. Une prestation hors catalogue n'est jamais bloquée : « Créer le produit dans Axonaut » (une étape du plan, décochable) ou « Prendre le produit le plus proche » en gardant le titre, la description et le prix de la ligne. L'encadré « Doutes » reprend ce que Claude n'a pas tranché et les infos à demander. Totaux : HT (remises déduites), TVA de chaque ligne (20 % par défaut), TTC. 3. Le client : cherché dans Axonaut par le SIRET puis par le nom (fiches synchronisées et recherche dans Axonaut) ; trouvé : rien à créer ; absent : il sera créé (prospect), avec les champs remplis depuis le registre public des entreprises (raison sociale, SIRET du siège, adresse ; Axonaut n'a pas de champ SIREN), les infos du R1, l'invitation de l'agenda et les notes ; un nom qui ressemble est signalé (« Oui, prendre celui-là » ou « Non, créer un nouveau client »). Les infos manquantes (e-mail, téléphone, adresse…) sont « à compléter », jamais bloquantes. « Créer le devis dans Axonaut » ouvre l'écran de validation : créer le client (s'il est absent), créer le contact, créer les produits hors catalogue, créer le devis (lignes, commentaire, chargé d'affaires = la personne qui valide si elle est utilisatrice d'Axonaut), marquer la carte faite ; chaque étape se décoche. Axonaut n'a pas de brouillon : le devis reçoit son numéro à la création et se finit à la main dans Axonaut (lien « Ouvrir le devis dans Axonaut »). Rien n'est envoyé au client ; aucune facture n'est jamais créée. « Écarter le brouillon » ferme les cartes « Préparer le devis » (« Reprendre » le rouvre).",
    data: [
      { what: 'Notes collées, lignes, client (brouillon du Cockpit)', source: 'manual' },
      { what: 'Catalogue des produits, fiches clients', source: 'axonaut' },
      { what: 'Fiche R1, infos du R1, invités du rendez-vous', source: 'calendar' },
    ],
    excludes: ['Les abonnements (hébergement, maintenance) : ils passent par Stripe, pas par le devis'],
    freshness: ['axonaut'],
    related: ['regle.quote_prepare', 'regle.quote_to_send', 'synchro.catalogue-axonaut', 'chiffres.paniers'],
  },
  {
    id: 'regle.project_step',
    kind: 'rule',
    ruleKey: 'project_step',
    name: 'Étape de projet',
    screens: ['À faire', 'Projets'],
    summary: 'La prochaine étape non cochée d’un projet en cours.',
    calculation:
      'Pour chaque projet ni terminé ni en pause : la première étape non cochée devient une tâche pour la personne qui la fait (sinon ' +
      who('projectPlan') +
      "), à la date prévue par le planning (aujourd'hui si elle est passée). Cocher l'étape (plan « étape faite ») ferme la carte et fait apparaître la suivante ; marquer la carte faite ne coche pas l'étape.",
    data: [
      { what: 'Étapes des projets', source: 'cockpit' },
      { what: 'Dates du planning', source: 'cockpit' },
    ],
    settings: ['Modèle d’étapes et personnes (Réglages)'],
    freshness: [],
  },
  {
    id: 'regle.meeting_schedule',
    kind: 'rule',
    ruleKey: 'meeting_schedule',
    name: 'Fixer le R3, R4 ou R5',
    screens: ['À faire', 'Projets'],
    summary: 'La période pour caler un rendez-vous de projet a commencé.',
    calculation:
      "Quand la période d'un R3, R4 ou R5 non fixé a commencé (date du planning + N jours, 14 par défaut), une carte « Fixer le R3 avec … » pour la personne du rôle « rendez-vous » (sinon " +
      who('projectPlan') +
      "), due au début de la période (« période dépassée » ensuite). Si le planning déplace la période, l'ancienne carte se ferme et une nouvelle arrive. Se ferme seule quand le rendez-vous est fixé (plan « Fixer le rendez-vous » ou rendez-vous « R3 … » vu dans l'agenda).",
    data: [
      { what: 'Périodes des rendez-vous des projets', source: 'cockpit' },
      { what: 'Rendez-vous des agendas', source: 'calendar' },
    ],
    settings: ['Longueur de la période (Réglages > Planning)', 'Rôle « rendez-vous »'],
    freshness: ['calendar'],
  },
  {
    id: 'regle.manual_subscription_invoice',
    kind: 'rule',
    ruleKey: 'manual_subscription_invoice',
    name: 'Facturer l’abonnement (hors Stripe)',
    screens: ['À faire'],
    summary: 'Une échéance d’abonnement facturé hors Stripe approche.',
    calculation:
      "Pour chaque date de facturation d'un abonnement hors Stripe (début, puis tous les « période » mois jusqu'à la fin), une carte N jours avant (délai de l'abonnement, sinon 7 jours), pour " +
      who('invoice') +
      ". Se ferme seule quand une facture Axonaut du client d'un montant proche (± 2 %, au moins 1 €) apparaît à ± 20 jours de la date, ou si l'abonnement s'arrête avant. Une période passée depuis plus de 20 jours sans carte n'est plus proposée. Aucune facture n'est créée par le Cockpit.",
    data: [
      { what: 'Abonnements hors Stripe', source: 'manual' },
      { what: 'Factures du client', source: 'axonaut' },
    ],
    settings: [`Délai par défaut (${SETTINGS})`, 'Délai propre à chaque abonnement', who.setting('invoice')],
    freshness: ['axonaut'],
  },
  {
    // Lot AS : pas une règle (personne ne la propose), mais une carte de « À faire » qu'on demande souvent.
    id: 'afaire.taches',
    kind: 'rule',
    name: 'Tâches ajoutées à la main (description et étapes)',
    screens: ['À faire', 'Assistant'],
    summary:
      'Carte « Tâche » ajoutée par quelqu’un de l’équipe (ou proposée par l’assistant puis ajoutée), avec une description et des étapes à cocher.',
    calculation:
      "Une tâche s'ajoute avec « Ajouter une tâche » (titre, pour qui, pour quand) ; « Plus de détails » donne le client (sans passer par un projet), le projet, la description et les étapes dès la création. Une tâche proposée par l'assistant arrive avec sa description et ses étapes, à relire avant « Ajouter ». Sur la carte : la pastille du client, l'avancement des étapes (« 2/5 », aussi en vue Semaine) et le début de la description. « Ouvrir » ou un clic sur le titre déplie la carte sur place : titre, client, projet, personne, date, description et étapes s'y modifient et s'enregistrent au fil de l'eau (« Enregistré »). Description : texte libre, retours à la ligne gardés, les adresses web (http, https, www.) deviennent des liens. Étapes : cocher, ajouter avec Entrée, modifier d'un clic, supprimer avec la croix, ranger avec la poignée ⋮⋮ (ou Alt + flèches) ; ce qui est fait est barré, la barre montre l'avancement ; 100 étapes au plus. Cocher toutes les étapes ne ferme jamais la carte : elle se ferme avec « Fait » (ou « Écarter »), et « Annuler » dans « Fait cette semaine » la rouvre avec sa description et ses étapes. En vue Semaine, un clic sur une carte ouvre le choix de la date, où « Ouvrir la carte » montre la carte dépliée dans la liste. La carte, ses étapes et sa description restent dans le Cockpit : rien ne part vers Axonaut ou le client ; chaque changement est noté dans le journal. Quand quelqu'un d'autre t'ajoute une tâche ou te passe une carte (n'importe laquelle), tu reçois une notification « Nouvelle tâche pour toi : <titre> » (cloche, et téléphone si « Nouvelle tâche pour moi » est activé), jamais pour une carte qu'on s'attribue soi-même. « Le prévenir » : dès que « Pour qui » n'est pas toi, la case « Prévenir <Prénom> » (décochée par défaut) envoie, après « Ajouter », un message Google Chat privé en ton nom ou un mail depuis contact@evolusite.fr à son adresse Evolusite ; le message est prérempli (« Je t'ai ajouté une tâche pour le 22/10 : … ») et modifiable, le lien vers la tâche est ajouté à la fin. Quand tu passes une carte à quelqu'un, la ligne « Carte passée à Inès. Prévenir par Google Chat / Mail · Envoyer · Non merci » s'affiche sous la carte. Le Cockpit retient ton dernier canal. Chat demande ton compte Google relié (« Mon compte Google ») ; le mail ne part qu'à un membre actif de l'équipe, jamais vers l'extérieur. Si le message ne part pas, la tâche reste ajoutée (ou la carte réattribuée) et le message d'erreur le dit ; chaque envoi est noté dans le journal. Sans réseau, « Prévenir » est grisé.",
    data: [{ what: 'Tâches, descriptions et étapes saisies dans le Cockpit', source: 'manual' }],
    excludes: [
      'Les cartes proposées par les règles (devis, factures, R1…) : ni description ni étapes',
      '« Prévenir » : jamais un client ni une adresse hors de l’équipe',
    ],
    settings: ['Nouvelle tâche pour moi (Notifications, propre à chaque membre)'],
    freshness: [],
  },
  {
    // Lot BI (retour d'Inès, décision de Maxime du 23/10/2026) : pas une règle, mais la suite de toute validation.
    id: 'afaire.annuler',
    kind: 'rule',
    name: 'Valider une carte : « Annuler » pendant 5 secondes',
    screens: ['À faire', 'Projets', 'Support', 'Agenda'],
    summary:
      'Après « Valider » (écran de validation, fenêtre mail) ou un geste direct (« Fait », case d’une étape), rien ne part pendant 5 secondes : « Annuler » dans le petit message du bas arrête tout, comme l’annulation d’envoi de Gmail.',
    calculation:
      "Au clic sur « Valider », la fenêtre se ferme et la carte disparaît tout de suite ; le message du bas dit ce qui va être fait (« 3 opérations exécutées », « Envoyé avec Gmail… ») avec « Annuler », un compte à rebours de 5 secondes et une barre qui se vide (Ctrl+Z ou ⌘Z marche aussi, hors d'un champ de saisie). Pendant ces 5 secondes, RIEN n'est exécuté : ni mail au client, ni Axonaut, ni agenda, ni changement dans le Cockpit ; les choix (étapes cochées, textes modifiés) sont seulement enregistrés. « Annuler » à temps : « Annulé : rien n'est parti. » et la carte revient comme avant. À la fin des 5 secondes, c'est le serveur qui exécute (fermer l'onglet ou perdre le réseau ne l'empêche pas, un redémarrage du serveur non plus : il reprend au démarrage), une seule fois ; ensuite l'écran se met à jour comme avant : message des conséquences avec « Voir », écran des résultats rouvert s'il y a des cartes changées, un lien à ouvrir (« Ouvrir le devis dans Axonaut ») ou une étape en erreur. « Annuler » arrivé trop tard : « Trop tard : l'action est déjà partie. ». Hors connexion, un « Fait » gardé sur l'appareil peut aussi être annulé tant qu'il n'est pas parti (il est retiré de la file). Les tâches du serveur (synchros, récap du matin, tâches planifiées) ne passent jamais par ce délai.",
    data: [{ what: 'Plans validés, en attente pendant le délai (statut « programmé »)', source: 'cockpit' }],
    excludes: [
      'Les tâches planifiées et les synchros du serveur : aucun délai',
      'Une fois le délai passé, une écriture faite à l’extérieur (mail parti, devis créé) ne s’annule plus depuis le Cockpit',
    ],
    freshness: [],
    related: ['synchro.hors-connexion'],
  },
  {
    // Lot BB : type et règle posés (contrat du 20/10/2026) ; les cartes sont créées par l'analyse (lot BD).
    id: 'regle.support_conversation',
    kind: 'rule',
    ruleKey: 'support_conversation',
    name: 'Support · un client attend une réponse',
    screens: ['À faire', 'Support'],
    summary: 'Une conversation Crisp ou un mail de support attend une réponse de l’équipe.',
    calculation:
      "Les conversations Crisp (toutes les 2 minutes) et les mails des clients des boîtes lues pour le support (toutes les 10 minutes, Réglages > Support) arrivent dans l'onglet Support. Une conversation « attend une réponse » quand c'est le client qui a écrit en dernier ; elle passe « en attente du client » dès qu'on lui a répondu ; une conversation traitée ou écartée revient si le client réécrit. Un mail n'est un mail de support que s'il vient de l'adresse d'un client (fiche Axonaut) ou d'un administrateur de son site ; jamais d'une adresse @evolusite.fr, jamais par le nom. La carte « Support · <client> » est proposée une fois la conversation analysée par Claude, pour " +
      who('support') +
      ' ; son bouton « Ouvrir la conversation » ouvre la conversation dans l’onglet Support. Rien n’est envoyé au client sans validation.',
    data: [
      { what: 'Conversations Crisp', source: 'crisp' },
      { what: 'Mails des clients (boîtes lues pour le support)', source: 'gmail' },
    ],
    excludes: [
      'Les mails des fournisseurs (OVH, Clever Cloud…) : vue Fournisseurs',
      'Les autres mails (boîte perso, newsletters) : ni lus ni gardés',
    ],
    settings: ['Boîtes lues et domaines fournisseurs (Réglages > Support)', who.setting('support')],
    freshness: ['crisp', 'gmail'],
  },
  {
    // Lot BB : type et règle posés (contrat du 20/10/2026) ; les cartes sont créées par le tri des fournisseurs (lot BG).
    id: 'regle.supplier_alert',
    kind: 'rule',
    ruleKey: 'supplier_alert',
    name: 'Alerte d’un fournisseur',
    screens: ['À faire', 'Support'],
    summary: 'Un mail important d’un fournisseur (OVH, Clever Cloud…) demande une action.',
    calculation:
      "Les mails des domaines fournisseurs (Réglages > Support) sont mis de côté à la synchro du support, puis triés : un mail important (domaine à valider avant une date, incident en cours…) devient une carte « <Fournisseur> · <objet> » par motif et par domaine, à l'échéance indiquée dans le mail (sinon aujourd'hui), pour " +
      who('supplier') +
      ' ; les autres sont archivés dans Gmail avec le libellé « Cockpit/Ignoré » (jamais supprimés). Son bouton « Ouvrir » ouvre la vue Fournisseurs de l’onglet Support.',
    data: [{ what: 'Mails des fournisseurs (boîtes lues pour le support)', source: 'gmail' }],
    settings: ['Domaines fournisseurs (Réglages > Support)', who.setting('supplier')],
    freshness: ['gmail'],
  },
  {
    // Lot BL : chance de signature d'un devis, le lendemain du R2.
    id: 'regle.quote_win_chance',
    kind: 'rule',
    ruleKey: 'quote_win_chance',
    name: 'Chance de signature',
    screens: ['À faire', 'Chiffres'],
    summary: 'Un R2 a eu lieu hier : dire en un clic quelle chance le devis du client a de signer.',
    calculation:
      "Apparaît le lendemain du dernier R2 passé d'un client (au plus 30 jours après), quand ce client a un devis en attente (hors devis à 0 €) dont la chance de signature n'est pas encore donnée. Rattachement du R2 au devis : le client du rendez-vous (relié à l'événement, au projet, ou reconnu par l'adresse d'un invité) ; à défaut, le seul client ayant un devis en attente dont le nom correspond au prospect du titre (« R2 - Menuiserie Gendreau » : même nom, accents, apostrophes et majuscules ignorés, ou nom de 6 caractères au moins contenu dans le titre ; deux clients possibles : aucun) ; puis son devis en attente le plus récent ; sans client reconnu ou sans devis en attente, pas de carte. Une carte par membre présent sur le R2 (agendas qui ont le rendez-vous, puis membres invités), sinon pour " +
      who('quoteToSend') +
      ". Un clic sur 10, 30, 50, 70 ou 90 % enregistre la chance sur le devis et fait la carte ; « Je ne sais pas encore » fait la carte sans rien changer (la chance reste inconnue). C'est un geste interne : rien ne part. Se ferme seule quand le devis est signé ou perdu, ou dès que la chance est donnée (par un autre membre présent, ou sur la ligne du devis dans « Simuler » des Chiffres). « Annuler » dans « Fait » remet la chance d'avant. Faite ou écartée, la carte d'une personne ne revient pas pour ce R2 (un nouveau R2 du client en repose une si la chance est toujours inconnue). " +
      ZERO,
    data: [
      { what: 'Rendez-vous R2 (membres présents, invités)', source: 'calendar' },
      { what: 'Devis en attente', source: 'axonaut' },
      { what: 'Chance de signature du devis', source: 'manual' },
    ],
    settings: [who.setting('quoteToSend')],
    freshness: ['calendar', 'axonaut'],
    related: ['chiffres.simulation', 'regle.quote_to_send', 'regle.quote_followup'],
  },
  {
    id: 'regle.mail_followup',
    kind: 'rule',
    ruleKey: 'mail_followup',
    name: 'Relancer un mail sans réponse',
    screens: ['À faire'],
    summary:
      'Un mail envoyé à un client attend une réponse (maquette, demande d’infos) et rien n’est revenu.',
    calculation:
      "Regarde les mails envoyés à un client depuis les boîtes lues (membres et contact@) qui sont les derniers de leur fil, de 2 à 14 jours après l'envoi : avant, le client a le temps de répondre et rien n'est lu. Claude lit chaque mail une seule fois et dit s'il attend une réponse, ce qui est attendu, et prépare un brouillon de relance. Pour info, merci, facture ou devis : pas de carte (les devis ont déjà « Relancer un devis »). La carte arrive au bout du délai réglé (7 jours par défaut), plus tôt si le mail dit lui-même que c'est urgent ou donne une date limite (2 jours au minimum), jamais un week-end. Juste avant, le fil est relu chez Gmail : si quelqu'un y a répondu, pas de carte. Pour la personne qui a envoyé le mail ; depuis contact@, pour " +
      who('support') +
      ". Rien ne part tout seul : « Préparer la relance » ouvre le brouillon, modifiable, envoyé dans le même fil après validation. Se ferme seule si le client répond dans le fil, ou sous le même objet depuis une autre boîte. S'il a écrit ailleurs (autre fil, Crisp), la carte le dit et reste ouverte. Une relance envoyée repart pour un cycle, trois relances au plus par fil. Faite ou écartée, la carte ne revient pas pour ce mail.",
    data: [
      { what: 'Mails envoyés aux clients', source: 'gmail' },
      { what: 'Réponses du client', source: 'gmail' },
      { what: 'Lecture du mail et brouillon', source: 'manual' },
    ],
    settings: [`Activée, et délai sans réponse (${SETTINGS})`, who.setting('support')],
    freshness: ['gmail'],
    related: ['regle.quote_followup', 'synchro.gmail'],
  },
] as const satisfies readonly GuideFiche[];
